import React, { FC } from "react";
import classnames from "classnames";

import "./index.scss";

/**
 * The bolt inside a reached rung (frame 9119:954 draws one in each earned pip and in the current
 * one). Inline rather than an imported asset, and filled with `currentColor`, so it takes the pip's
 * own colour -- this app's `Icon` assets bake their colour into the file, which is exactly the
 * constraint that keeps one of them usable on a single background.
 *
 * Decorative: the pip's state is already carried by its class and by the captioned tier name, so this
 * is hidden from assistive tech rather than announced as an unlabelled graphic eight times a row.
 */
const SparkGlyph: FC = () => (
  <svg className="ladder-pip-spark" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M13 2 4.1 12.1a1 1 0 0 0 .77 1.64h5.64l-1.5 7.25a1 1 0 0 0 1.78.81L19.9 11.9a1 1 0 0 0-.77-1.64h-5.64l1.5-7.25A1 1 0 0 0 13 2Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * One rung of a per-value ladder row, classed by its position relative to the current tier
 * (overview-rebuild-pr2-plan.md's 0-based pip rule, computed by `ValueLadderCards`): `"earned"`
 * before the current tier's index, `"current"` at the index itself, `"locked"` after — or every
 * position `"locked"` when there is no current tier yet.
 */
export type LadderPipState = "earned" | "current" | "locked";

interface CoreValueCardProps {
  valueTitle: string;
  totalScore: number;
  coreValueId: number;
  onClickHandler: (coreValueId: number) => void;
  isSelected?: boolean;
  iconUrl: string;
  // Always supplied by ValueLadderCards now — `ladderTierLabel`/`ladderSubtitle` already handle the
  // null-current-tier case ("Not started" / the pointsToNext copy), so there is no longer a
  // props-optional legacy render mode here. The old else-branch ("{totalScore} sparks | ... to get
  // reward") was dead code — ValueLadderCards always computed both, and ladderTierLabel always
  // returns a string, so it could never actually render (overview-rebuild-pr2-plan.md) — removed
  // along with the optionality on these two props.
  tierLabel: string;
  pipStates: LadderPipState[];
  // The current tier's own entry threshold, captioned under its pip — NOT "points to next" (that's
  // `subtitle`, kept deliberately as the distance-remaining signal). `null` when there is no current
  // tier, so no pip is `"current"` and nothing is captioned.
  currentTierThreshold: number | null;
}

/**
 * One core value's ladder row on the My Sparks tab (overview-rebuild-pr2-plan.md, design node
 * 9119:950): badge art, the value's title over its current tier name, a track of tier pips
 * (earned/current/locked) with the current tier's threshold captioned under its own pip, and the
 * value's total score right-aligned. Clicking the row toggles the received-recognitions filter via
 * `onClickHandler` — the filter state and the handler both live in `MySparksContainer`; this stays
 * presentational.
 */
const CoreValueCard: FC<CoreValueCardProps> = (props) => {
  const {
    valueTitle,
    totalScore,
    coreValueId,
    onClickHandler,
    isSelected,
    iconUrl,
    tierLabel,
    pipStates,
    currentTierThreshold,
  } = props;

  return (
    <div
      className={classnames("core-value-card", isSelected ? "selected" : "")}
      onClick={() => onClickHandler(coreValueId)}
    >
      <div className="value-icon-section">
        <div className="icon" style={{ backgroundImage: `url("${iconUrl}")` }} />
      </div>
      <div className="value-heading-section">
        <div className="value-title-section">{valueTitle}</div>
        <div className="value-tier-label">{tierLabel}</div>
      </div>
      <div className="value-ladder-track">
        {pipStates.map((state, index) => (
          <span
            key={index}
            data-testid={`ladder-pip-${coreValueId}-${index}`}
            className={classnames("ladder-pip", `ladder-pip-${state}`)}
          >
            {/* Frame 9119:950 puts TWO text nodes under the current pip -- the tier's name (9130:1167)
                above its threshold (9130:1168). The name is deliberately repeated here even though the
                row heading already carries it: the caption names the rung the pip marks, which is what
                makes an eight-pip track readable without counting. */}
            {state === "earned" || state === "current" ? <SparkGlyph /> : null}
            {state === "current" ? (
              <span className="ladder-pip-caption">
                <span className="ladder-pip-caption-name">{tierLabel}</span>
                {currentTierThreshold !== null ? (
                  <span className="ladder-pip-caption-threshold">{currentTierThreshold}</span>
                ) : null}
              </span>
            ) : null}
          </span>
        ))}
      </div>
      <div className="value-score-section">{totalScore}</div>
    </div>
  );
};

export default CoreValueCard;
